import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Landing from './pages/Landing';
import Explore from './pages/Explore';
import DebateView from './pages/DebateView';
import Results from './pages/Results';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ConfirmEmail from './pages/ConfirmEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import EmailVerified from './pages/EmailVerified';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
// import Enterprise from './pages/Enterprise';
import Arena from './pages/Arena';
import AuthDiagnostic from './pages/AuthDiagnostic';
import About from './pages/About';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

// Inner component that has access to auth context
function AppRoutes() {
  const { user, loading } = useAuth();
  const loaderDismissed = useRef(false);

  // Dismiss the HTML loading screen only once auth has resolved.
  // While loading is true, #app-loading stays visible so there is no white flash.
  useEffect(() => {
    if (loading || loaderDismissed.current) return;
    loaderDismissed.current = true;
    const htmlLoader = document.getElementById('app-loading');
    if (!htmlLoader) return;
    htmlLoader.classList.add('fade-out');
    const t = setTimeout(() => htmlLoader.remove(), 250);
    return () => clearTimeout(t);
  }, [loading]);

  // Use user ID as key to force remount when auth state changes
  // This ensures all components get fresh state after login/logout
  const authKey = user?.id || 'anonymous';
  
  if (loading) {
    // Auth is still initialising — keep #root empty so the HTML loader stays shown.
    return null;
  }
  
  return (
    <Layout>
      <Routes key={authKey}>
        <Route path="/" element={<Landing />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/debate/:id" element={<DebateView />} />
        <Route path="/results/:id" element={<Results />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/email-verified" element={<EmailVerified />} />
          <Route path="/auth/callback" element={<EmailVerified />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/start" element={<Navigate to="/arena" replace />} />
        <Route
          path="/arena"
          element={
            <ProtectedRoute>
              <Arena />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* <Route path="/enterprise" element={<Enterprise />} /> */}
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/auth-diagnostic" element={<AuthDiagnostic />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
      <Analytics />
    </Router>
  );
}

export default App;